import React, {useEffect, useRef, useState} from "react";
import {formatTime24h} from "@/utilities/timeUtil";
import fileNamed from "@/app/components/file-named";
import {useAppDispatch, useAppSelector} from "@/store/hooks";
import {chatUISliceActions} from "@/store/features/chat/chatUiSlice";
import {ChatImage} from "@/app/components/chat-image";
import MarkdownRenderer from './chat-renderer/markdown-renderer';
import {ImageLightbox} from "@/app/components/image-lightbox";

interface UserMessageProps {
    content: string;
    images?: string[];
    timestamp: string;
    compactMobileSpacing?: boolean;
}

const UserMessage = function UserMessage({content, images, timestamp, compactMobileSpacing = false}: UserMessageProps) {
    const formattedTime = formatTime24h(timestamp);
    const dispatch = useAppDispatch();
    const uploadingFiles = useAppSelector(state => state.uploadingFiles.files);
    const fileTag = "[FILE_UPLOAD]";
    const fileContextText = "File uploaded with name:"
    const previousFileStatus = useRef<string | null | undefined>(null);
    const [animationStage, setAnimationStage] = useState<'initial' | 'fadeIn' | 'slide' | 'compress'>('initial');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImageUrl, setLightboxImageUrl] = useState<string>('');

    const hasMarkdownSyntax = (text: string): boolean => {
        const markdownPatterns = [
            /\*\*.*?\*\*/,     // Bold **text**
            /\*.*?\*/,         // Italic *text* (excluding ** which is already caught)
            /`.*?`/,           // Inline code `text`
            /^#+\s/m,          // Headers # ## ###
            /^\* /m,           // Bullet lists
            /^\d+\. /m,        // Numbered lists
            /^\> /m,           // Blockquotes
            /\[.*?\]\(.*?\)/,  // Links [text](url)
        ];

        return markdownPatterns.some(pattern => pattern.test(text));
    };

    // Efecto para la animación de entrada
    useEffect(() => {
        // Aplicar un pequeño retraso antes de iniciar el fade in
        const fadeInTimer = setTimeout(() => {
            setAnimationStage('fadeIn');
        }, 50);

        return () => clearTimeout(fadeInTimer);
    }, []);

    function contentIsAFile(content: string): boolean {
        return content.includes(fileTag);
    }

    function getFileName(content: string): string {
        if (content.includes(fileTag)) {
            // Extract filename from format: [FILE_UPLOAD] File uploaded with name:filename.ext
            const afterTag = content.split(fileContextText)[1];
            if (!afterTag) return "";
            
            // Get the filename (everything until newline or end of string)
            const filename = afterTag.split('\n')[0].trim();
            return filename;
        }
        return "";
    }

    function extractFileExtension(filename: string) {
        const lastDotPosition = filename.lastIndexOf('.');
        if (lastDotPosition === -1 || lastDotPosition === 0) {
            return '';
        }
        return filename.substring(lastDotPosition + 1);
    }

    function getUploadingFileStatus(filename: string) {
        return uploadingFiles.find(file => filename.includes(file.name))?.status;
    }

    const removeMessageWithAnimation = (messageContent: string) => {
        // Etapa 1: Desplazamiento horizontal
        setTimeout(() => {
            setAnimationStage('slide');
            setTimeout(() => {
                // Etapa 2: Compresión vertical
                setAnimationStage('compress');

                // Espera a que termine la segunda animación antes de eliminar el mensaje
                setTimeout(() => {
                    dispatch(chatUISliceActions.removeFileMessage(messageContent));
                }, 700);
            }, 800);
        }, 3000)

        // Espera a que termine la primera animación antes de iniciar la segunda
    };

    const getAnimationClass = () => {
        switch (animationStage) {
            case 'initial':
                return 'opacity-0 translate-x-4 scale-y-100 my-4'; // Estado inicial: invisible y ligeramente desplazado hacia abajo
            case 'fadeIn':
                return 'opacity-100 translate-y-0 scale-y-100 my-4'; // Fade in y movimiento hacia arriba
            case 'slide':
                return 'opacity-0 transform translate-x-10 scale-y-100 my-4';
            case 'compress':
                return 'opacity-0 transform translate-x-10 scale-y-0 my-0';
            default:
                return 'opacity-100 scale-y-100';
        }
    };

    const getHeightClass = () => {
        switch (animationStage) {
            case 'compress':
                return 'max-h-0';
            default:
                return ''; // No height restriction for normal states
        }
    };

    useEffect(() => {
        if (contentIsAFile(content)) {
            const filename = getFileName(content);
            const currentStatus = getUploadingFileStatus(filename);

            // Check if the file status is 'error' and it has changed from previous status
            if (currentStatus === 'error' && previousFileStatus.current !== 'error') {
                removeMessageWithAnimation(content);
            }

            // Update the previous status ref for next comparison
            previousFileStatus.current = currentStatus;
        }
    }, [uploadingFiles, content, dispatch]);

    return (
        <div
            className={`${compactMobileSpacing ? 'ml-2 md:ml-24 mr-2 md:mr-6' : 'ml-24 mr-6'} flex flex-col items-end transition-all duration-500 ease-in-out overflow-hidden ${getAnimationClass()} ${getHeightClass()}`}>
            <div className="mt-3 grid gap-2">
                {images?.map((image, index) => (
                    <ChatImage 
                        key={index} 
                        imageUrl={image}
                        enableLightbox={true}
                        onClick={() => {
                            setLightboxImageUrl(image);
                            setLightboxOpen(true);
                        }}
                    />
                ))}
            </div>

            <div className="inline-block max-w-3xl text-right">
                <div
                    className="user-message-bubble max-w-[768px] p-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-white/40 hover:bg-white/90 hover:border-white/60 transition-all duration-200 group shadow-sm">
                    <div className="flex-1">
                        {contentIsAFile(content) ?
                            fileNamed(
                                extractFileExtension(getFileName(content)),
                                getFileName(content),
                                getUploadingFileStatus(getFileName(content))
                            )
                            :
                            <div className="max-h-96 overflow-y-auto">
                                {hasMarkdownSyntax(content) ? (
                                    <MarkdownRenderer
                                        content={content}
                                        className="user-message-prose"
                                    />
                                ) : (
                                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                        {content}
                                    </p>
                                )}
                            </div>}
                    </div>
                </div>
                <p className="text-xs text-gray-500 mt-1 text-right mr-2">{formattedTime}</p>
            </div>
            
            {lightboxImageUrl && (
                <ImageLightbox
                    imageUrl={lightboxImageUrl}
                    isOpen={lightboxOpen}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </div>
    );
}

export default UserMessage;